import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tv_newsletter_popup_dismissed";
const DELAY_MS = 30000;

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), name: name.trim() || null });
      setStatus("success");
      setMessage("Benvenuta! Ti ho appena scritto — controlla la tua posta 💌");
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Qualcosa non ha funzionato. Riprova.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#3D2140]/50 backdrop-blur-sm"
          data-testid="newsletter-popup"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[520px] bg-white rounded-[28px] shadow-2xl overflow-hidden"
          >
            <button
              onClick={close}
              aria-label="Chiudi"
              data-testid="newsletter-popup-close"
              className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-[#FAF8F5] hover:bg-[#FF2768] hover:text-white flex items-center justify-center text-[#3D2140] transition-colors"
            >
              <X size={16} />
            </button>

            <div className="p-8 md:p-10">
              {status !== "success" ? (
                <>
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#FF2768] font-semibold mb-4">
                    <Mail size={12} /> Newsletter Crescere con Cura
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl leading-[1] text-[#3D2140] mb-3">
                    Riflessioni,<br/><span className="text-[#FF2768]">spunti e strumenti.</span>
                  </h3>
                  <p className="text-sm text-[#3D2140]/70 leading-relaxed mb-6">
                    Ogni due settimane, un piccolo appuntamento dedicato alle famiglie e alle tate. Nessuno spam, solo cura autentica.
                  </p>

                  <form onSubmit={submit} className="space-y-3">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Il tuo nome (facoltativo)"
                      className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] placeholder:text-[#3D2140]/40 focus:outline-none focus:ring-2 focus:ring-[#FF2768]/40"
                      data-testid="newsletter-popup-name"
                    />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="La tua email *"
                      className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-[#3D2140] placeholder:text-[#3D2140]/40 focus:outline-none focus:ring-2 focus:ring-[#FF2768]/40"
                      data-testid="newsletter-popup-email"
                    />

                    <label className="flex items-start gap-2 text-xs text-[#3D2140]/70 leading-relaxed cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        required
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 accent-[#FF2768]"
                        data-testid="newsletter-popup-consent"
                      />
                      <span>Ho letto la <a href="/privacy" className="underline hover:text-[#FF2768]">Privacy</a> e acconsento a ricevere la newsletter. Posso disiscrivermi in qualsiasi momento.</span>
                    </label>

                    {status === "error" && (
                      <p className="text-xs text-[#FF2768] font-medium">{message}</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "loading" || !consent}
                      data-testid="newsletter-popup-submit"
                      className="w-full mt-2 px-6 py-3.5 rounded-full bg-[#FF2768] hover:bg-[#c81e52] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold uppercase tracking-wider transition-colors"
                    >
                      {status === "loading" ? "Un attimo…" : "Iscrivimi"}
                    </button>
                  </form>

                  <button
                    onClick={close}
                    data-testid="newsletter-popup-later"
                    className="mt-4 w-full text-xs text-[#3D2140]/50 hover:text-[#3D2140] underline underline-offset-4"
                  >
                    Magari un'altra volta
                  </button>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#81C3C9]/30 flex items-center justify-center mb-4">
                    <Check size={28} className="text-[#3D2140]" />
                  </div>
                  <h3 className="font-display text-3xl text-[#3D2140] mb-2">Benvenuta.</h3>
                  <p className="text-sm text-[#3D2140]/70 mb-6">{message}</p>
                  <button
                    onClick={close}
                    data-testid="newsletter-popup-close-success"
                    className="px-6 py-2.5 rounded-full bg-[#3D2140] hover:bg-[#5A3660] text-white text-sm font-semibold"
                  >
                    Continua a esplorare
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
